<template>
  <popup-dialog :popup-active="open" class="popup-window">
    <div
      ref="dialog"
      class="confirm confirm--bg-white confirm--wide source-documents-popup"
      role="dialog"
      aria-modal="true"
      aria-labelledby="source-documents-popup-title"
      @keydown.esc="emit('close')"
    >
      <button
        ref="closeButton"
        type="button"
        class="source-documents-popup__close"
        :aria-label="t('popup.content.sources.close')"
        @click="emit('close')"
      >
        <i class="fas fa-times confirm-close" aria-hidden="true"></i>
      </button>
      <h2
        id="source-documents-popup-title"
        class="typo-item fs--26 lh--36 fw--500 text-center mb--15"
      >
        {{ t('popup.content.sources.title') }}
      </h2>
      <p class="typo-item text-center mb--34">{{ t('popup.content.sources.hint') }}</p>
      <source-document-list
        :documents="documents"
        :uploading="uploading"
        :error="error"
        @upload="emit('upload', $event)"
        @attach-url="emit('attach-url', $event)"
        @remove="emit('remove', $event)"
      ></source-document-list>
    </div>
  </popup-dialog>
</template>

<script setup lang="ts">
// The content form's popup for a point's source documents, over the settings
// popup. Presentational, like the list inside it: the view that opens it owns
// the documents and carries out what this emits.

import { nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import PopupDialog from '@/components/ui/popup/PopupDialog.vue';
import SourceDocumentList from './SourceDocumentList.vue';
import type { SourceDocument } from '../types/SourceDocumentTypes';

const props = defineProps<{
  open: boolean;
  documents: SourceDocument[];
  uploading: boolean;
  /** The backend's reason a call failed, '' for none given, null when none did. */
  error: string | null;
}>();
const emit = defineEmits<{
  (event: 'close'): void;
  (event: 'upload', file: File): void;
  (event: 'attach-url', url: string): void;
  (event: 'remove', documentId: number): void;
}>();

const { t } = useI18n();
const closeButton = ref<HTMLButtonElement | null>(null);

// Into the dialog on opening, so Escape reaches it and a keyboard user starts
// inside; the trigger takes focus back on closing.
watch(
  () => props.open,
  isOpen => {
    if (isOpen) void nextTick(() => closeButton.value?.focus());
  }
);
</script>

<style scoped lang="scss">
.source-documents-popup {
  text-align: left;
}

.source-documents-popup__close {
  border: 0;
  background: none;
  padding: 0;
}
</style>
